// THE NUMERIC VALUE IS THE PRECEDENCE RANK: higher wins. When several playlists are active for the same
// location and surface at once, the most specific target wins and the rest are suppressed.
export const PlaylistScheduleScope = {
    Brand: 1,
    LocationGroup: 2,
    Location: 3,
} as const;

export type PlaylistScheduleScope = (typeof PlaylistScheduleScope)[keyof typeof PlaylistScheduleScope];

// EVERY schedule recurs — there is no one-time mode. What varies is the daily window, and it has exactly
// three shapes on the wire:
//
//   dayPartId set, minutes null   hours come from the day part, resolved per location
//   both minutes set              explicit hours; endMinute < startMinute wraps past midnight
//   all three null                ALL DAY — the whole of every day the recurrence matches
//
// The distinction matters in the UI: all-day is a deliberate choice, not a schedule missing its times.
export type WindowSource = 'allDay' | 'times' | 'dayPart';

export const windowSourceOf = (s: { dayPartId?: string | null; startMinute?: number | null }): WindowSource =>
    s.dayPartId ? 'dayPart' : s.startMinute != null ? 'times' : 'allDay';

export const RecurrenceFrequency = {
    Daily: 0,
    Weekly: 1,
    Monthly: 2,
} as const;

export type RecurrenceFrequency = (typeof RecurrenceFrequency)[keyof typeof RecurrenceFrequency];

// Bit field. Positions match JS Date.getDay() / .NET DayOfWeek (Sunday = 0).
export const RecurrenceDays = {
    None: 0,
    Sunday: 1,
    Monday: 2,
    Tuesday: 4,
    Wednesday: 8,
    Thursday: 16,
    Friday: 32,
    Saturday: 64,
} as const;

export const WEEKDAY_BITS = [
    RecurrenceDays.Sunday,
    RecurrenceDays.Monday,
    RecurrenceDays.Tuesday,
    RecurrenceDays.Wednesday,
    RecurrenceDays.Thursday,
    RecurrenceDays.Friday,
    RecurrenceDays.Saturday,
];

export interface PlaylistScheduleTarget {
    id: string;
    scope: PlaylistScheduleScope;
    targetId: string;
}

export interface SaveScheduleTarget {
    scope: PlaylistScheduleScope;
    targetId: string;
}

// EVERY DATE AND TIME HERE IS LOCAL WALL-CLOCK AT THE TARGET LOCATION, never an instant. A brand-wide
// 07:00-11:00 schedule fires at 07:00 in each store's own timezone.
//
// That is why these are strings and minute-of-day numbers rather than Date objects: constructing a Date here
// would attach the BROWSER's timezone, and a promo scheduled by someone in Chicago would then run on Chicago
// hours in a Los Angeles store. Never `new Date(...)` these values — format and parse them as plain text.
//
//   startDate / recurrenceEndDate "yyyy-MM-dd"           (matches <input type="date">)
//   startMinute / endMinute       minutes from local midnight, 0-1439
//
// endMinute < startMinute means the window WRAPS PAST MIDNIGHT (22:00-02:00 is 1320 -> 120).
export interface PlaylistSchedule {
    id: string;
    playlistId: string;
    enabled: boolean;

    startDate?: string | null;
    recurrenceEndDate?: string | null;
    startMinute?: number | null;
    endMinute?: number | null;

    // When set, the window comes from this day part as resolved for each target location, so a store with its
    // own breakfast hours gets them without anyone editing the schedule.
    dayPartId?: string | null;

    recurrenceFrequency?: RecurrenceFrequency | null;
    recurrenceInterval?: number | null;
    recurrenceDaysOfWeek?: number | null;
    recurrenceDayOfMonth?: number | null;

    targets: PlaylistScheduleTarget[];

    created: string;
    createdBy: string;
    updated?: string | null;
    updatedBy?: string | null;
}

export interface SavePlaylistSchedule {
    enabled: boolean;
    startDate?: string | null;
    recurrenceEndDate?: string | null;
    startMinute?: number | null;
    endMinute?: number | null;
    dayPartId?: string | null;
    recurrenceFrequency?: RecurrenceFrequency | null;
    recurrenceInterval?: number | null;
    recurrenceDaysOfWeek?: number | null;
    recurrenceDayOfMonth?: number | null;
    targets: SaveScheduleTarget[];
}

// ----- minute-of-day <-> "HH:mm" for <input type="time"> -----
// Deliberately string maths, never a Date: a Date would drag the browser's timezone into a value that has no
// timezone.

export const minuteToTime = (minute?: number | null): string => {
    if (minute == null) return '';
    const h = Math.floor(minute / 60);
    const m = minute % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const timeToMinute = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value);
    if (!match) return null;
    const minute = Number(match[1]) * 60 + Number(match[2]);
    return minute >= 0 && minute <= 1439 ? minute : null;
};

export const hasDay = (days: number | null | undefined, bit: number) => ((days ?? 0) & bit) !== 0;

export const toggleDay = (days: number | null | undefined, bit: number, on: boolean) =>
    on ? (days ?? 0) | bit : (days ?? 0) & ~bit;
